import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import moment from 'moment';

import DialogModal from '../../../../../components/DialogModal/DialogModal';
import InputLabel from '../../../../../components/v2/InputLabel';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { SocialPlatform } from '../../../../Model/CoachOnboarding';
import User from '../../../../../Model/User';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import { validationSchema } from './validation';
import {
  Container,
  Title,
  StyledTextField,
  StyledSelectField,
} from './styles';
import texts from './texts.json';

const DEFAULT_LAUNCH_DAYS_OFFSET = 14;

const NewCoachModal = ({
  onAddNewCoach,
  ...modalProps
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [primarySocial, setPrimarySocial] = useState(SocialPlatform.INSTAGRAM);
  const [primaryHandle, setPrimaryHandle] = useState('');
  const [alternatePlatform, setAlternatePlatform] = useState('');
  const [errors, setErrors] = useState({});
  const [CAMorCPSUsers, setCAMorCPSUsers] = useState([]);
  const {
    userDoc: loggedInUserDoc,
  } = useContext(LoggedInUserContext);
  const [selectedCAMUserId, setSelectedCAMUserId] = useState(loggedInUserDoc.id);
  const [selectedCPSUserId, setSelectedCPSUserId] = useState();
  const [coachDriveFolder, setCoachDriveFolder] = useState('');
  const [localLaunchDate, setLocalLaunchDate] = useState(
    moment().local().add(DEFAULT_LAUNCH_DAYS_OFFSET, 'days').toDate(),
  );

  const [isLoading, setIsLoading] = useState(false);
  const isComponentMountedRef = useComponentMounted();

  const isAlternatePlatformSelected = primarySocial === SocialPlatform.OTHER;

  // Fetch CAM users
  useEffect(() => {
    const fetchInternalUsers = async () => {
      setIsLoading(true);
      const users = await User.getInternalUsers();
      if (isComponentMountedRef.current) {
        const CAMandCPSUsers = users.filter(
          (user) => (user.isCPS || user.isCAM),
        );
        setCAMorCPSUsers(CAMandCPSUsers);
        setIsLoading(false);
      }
    };
    fetchInternalUsers();
  }, [isComponentMountedRef]);

  const renderSocialOptions = useMemo(() => (
    Object.entries(SocialPlatform).map(([key, social]) => (
      <MenuItem key={key} value={social}>{social}</MenuItem>
    ))
  ), []);

  const CAMorCPSuserOptions = useMemo(() => (
    Object.entries(CAMorCPSUsers).map(([key, user]) => (
      <MenuItem key={key} value={user.id}>{`${user.name} (${user.email})`}</MenuItem>
    ))
  ), [CAMorCPSUsers]);

  const onSave = useCallback(() => {
    try {
      const launchDate = moment(localLaunchDate).startOf('day').toDate();
      const formData = {
        name,
        email,
        primarySocial: isAlternatePlatformSelected ? alternatePlatform : primarySocial,
        primaryHandle,
        coachDriveFolder,
        launchDate,
      };
      validationSchema.validateSync(formData, { abortEarly: false });
      onAddNewCoach(
        email,
        name,
        isAlternatePlatformSelected ? alternatePlatform : primarySocial,
        primaryHandle,
        selectedCAMUserId,
        selectedCPSUserId,
        coachDriveFolder,
        launchDate,
      );
    } catch (err) {
      const errorMessages = err.inner.reduce((messages, errorObj) => ({
        ...messages,
        [errorObj.path]: errorObj.message,
      }), {});
      setErrors(errorMessages);
    }
  }, [
    name,
    email,
    primarySocial,
    primaryHandle,
    isAlternatePlatformSelected,
    alternatePlatform,
    onAddNewCoach,
    selectedCAMUserId,
    selectedCPSUserId,
    coachDriveFolder,
    localLaunchDate,
  ]);

  // Reset the state when the modal is opened
  useEffect(() => {
    if (modalProps.isOpen) {
      setName('');
      setEmail('');
      setPrimarySocial(SocialPlatform.INSTAGRAM);
      setPrimaryHandle('');
      setAlternatePlatform('');
      setErrors({});
      setSelectedCAMUserId(loggedInUserDoc.id);
      setSelectedCPSUserId('');
    }
  }, [modalProps.isOpen, loggedInUserDoc]);

  return (
    <>
      <DialogModal
        open={modalProps.isOpen}
        onClose={modalProps.onDidDismiss}
        maxWidth="sm"
        fullWidth
      >
        <Container>
          <Title>{texts.title}</Title>
          <StyledTextField
            label={texts.name}
            variant="outlined"
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          />
          <StyledTextField
            label={texts.email}
            variant="outlined"
            onChange={(e) => setEmail(e.target.value?.toLowerCase())}
            error={!!errors.email}
            helperText={errors.email}
          />
          <InputLabel>{texts.primarySocial}</InputLabel>
          <StyledSelectField
            value={primarySocial}
            onChange={(e) => setPrimarySocial(e.target.value)}
          >
            {renderSocialOptions}
          </StyledSelectField>
          {isAlternatePlatformSelected && (
          <StyledTextField
            label={texts.otherSocial}
            variant="outlined"
            onChange={(e) => setAlternatePlatform(e.target.value.toUpperCase())}
            error={!!errors.primarySocial}
            helperText={errors.primarySocial}
          />
          )}
          <StyledTextField
            label={texts.primaryHandle}
            variant="outlined"
            onChange={(e) => setPrimaryHandle(e.target.value)}
            error={!!errors.primaryHandle}
            helperText={errors.primaryHandle}
          />
          <StyledTextField
            label={texts.coachDriveFolder}
            variant="outlined"
            placeholder={texts.coachDriveFolderPlaceholder}
            onChange={(e) => setCoachDriveFolder(e.target.value)}
            error={!!errors.coachDriveFolder}
            helperText={errors.coachDriveFolder}
          />
          <DatePicker
            label={texts.launchDate}
            value={localLaunchDate}
            onChange={(newValue) => setLocalLaunchDate(newValue)}
            minDate={moment().local()}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />
          <InputLabel>{texts.assignCAM}</InputLabel>
          <StyledSelectField
            value={selectedCAMUserId}
            onChange={(e) => setSelectedCAMUserId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {CAMorCPSuserOptions}
          </StyledSelectField>
          <InputLabel>{texts.assignCPS}</InputLabel>
          <StyledSelectField
            value={selectedCPSUserId}
            onChange={(e) => setSelectedCPSUserId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {CAMorCPSuserOptions}
          </StyledSelectField>
          <Button
            onClick={onSave}
            variant="contained"
          >
            {texts.save}
          </Button>
        </Container>
      </DialogModal>
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

NewCoachModal.propTypes = {
  onAddNewCoach: PropTypes.func.isRequired,
};

export default NewCoachModal;
